import { FC, PropsWithChildren, useLayoutEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import AppLogo from '@/components/ui/AppLogo/AppLogo.tsx';
import Navbar from '@/layouts/Default/Navbar.tsx';
import { Button, Drawer } from 'react-daisyui';
import { XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { DefaultLayoutProvider, useDefaultLayoutContext } from '@/layouts/Default/DefaultLayoutProvider.tsx';
import Portal from '@/components/shared/Portal/Portal.tsx';
import { cn } from '@/utils/classNames.ts';
import { usePathname } from '@/hooks/usePathname.ts';
import NavbarChat from '@/layouts/Default/NavbarChat.tsx';

type LayoutProps = {
  mainClassName?: string;
};
const Layout: FC<LayoutProps> = (props) => {
  return (
    <DefaultLayoutProvider>
      <LayoutInner {...props} />
    </DefaultLayoutProvider>
  );
};

const LayoutInner: FC<LayoutProps> = ({ mainClassName }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const { containerElementRef } = useDefaultLayoutContext();
  const pathname = usePathname();

  return (
    <>
      <div className="flex grow min-h-screen flex-col lg:flex-row text-text-primary">
        <aside className="flex lg:flex-col gap-6 px-4 py-2 lg:px-6 lg:py-10 lg:w-[288px] lg:min-w-[288px] drop-shadow-xl max-h-[100dvh] sticky top-0 z-10 bg-neutral-50">
          <div className="lg:px-4 flex flex-col justify-center">
            <Link to={Routes.DASHBOARD}>
              <AppLogo.Default className="w-[180px]" />
            </Link>
          </div>
          <div className="flex grow flex-col justify-center items-end lg:justify-start lg:items-stretch">
            {pathname === Routes.DASHBOARD ? (
              <NavbarChat className="hidden lg:flex" />
            ) : (
              <Navbar className="hidden lg:flex" />
            )}
            <Button shape="circle" onClick={() => setIsDrawerOpen((prev) => !prev)} className="p-3 lg:hidden">
              <Bars3Icon />
            </Button>
          </div>
        </aside>
        <main
          ref={containerElementRef}
          id="default-layout-content-container"
          className={cn(
            'z-0 bg-base-200 relative flex flex-col grow gap-4 md:gap-8 lg:max-h-screen lg:overflow-auto p-4 md:p-6 lg:max-w-[calc(100%-288px)]',
            mainClassName
          )}
        >
          <div
            id="layout-top-sticky"
            className="fixed top-16 lg:top-0 right-0 z-10 w-full lg:max-w-[calc(100%-288px)] empty:hidden pointer-events-none"
          ></div>
          <Outlet />
        </main>
      </div>
      <Drawer
        className="z-10"
        end={true}
        side={
          <div className="flex flex-col max-w-[100vw] max-h-[100dvh] h-[100dvh] bg-white overflow-auto">
            <div className="bg-white pt-6 pb-4 px-6 flex justify-end sticky top-0 z-10">
              <Button
                color="ghost"
                size="sm"
                endIcon={<XMarkIcon className="w-6 h-6" />}
                onClick={() => setIsDrawerOpen(false)}
              >
                {t('button.close')}
              </Button>
            </div>
            <div className="flex grow pb-6 px-6">
              {pathname === Routes.DASHBOARD ? (
                <NavbarChat onClick={() => setIsDrawerOpen(false)} className="w-[250px] max-w-full" />
              ) : (
                <Navbar onClick={() => setIsDrawerOpen(false)} className="w-[250px] max-w-full" />
              )}
            </div>
          </div>
        }
        open={isDrawerOpen}
        onClickOverlay={() => (isDrawerOpen ? setIsDrawerOpen(false) : null)}
      ></Drawer>
      <LayoutDrawer />
    </>
  );
};

const InnerDrawer: FC<PropsWithChildren> = ({ children }) => {
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  return (
    <Portal watch={watch} elementId={'layout-drawer'}>
      {children}
    </Portal>
  );
};

const LayoutTopSticky: FC<PropsWithChildren> = ({ children }) => {
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  return (
    <Portal watch={watch} elementId="layout-top-sticky">
      {children}
    </Portal>
  );
};

const LayoutDrawer: FC = () => {
  const { isDrawerOpen, setIsDrawerOpen } = useDefaultLayoutContext();
  const { t } = useTranslation();

  return (
    <Drawer
      className="z-10"
      end={true}
      side={
        <div className="flex flex-col max-w-[100vw] max-h-[100dvh] h-[100dvh] bg-white overflow-auto">
          <div className="bg-white pt-6 pb-4 px-6 flex justify-end sticky top-0 z-10">
            <Button
              color="ghost"
              size="sm"
              endIcon={<XMarkIcon className="w-6 h-6" />}
              onClick={() => setIsDrawerOpen(false)}
            >
              {t('button.close')}
            </Button>
          </div>
          <div id="layout-drawer" className="empty:hidden pb-6 px-6" />
        </div>
      }
      open={isDrawerOpen}
      onClickOverlay={() => (isDrawerOpen ? setIsDrawerOpen(false) : null)}
    ></Drawer>
  );
};

export default {
  Layout,
  Drawer: InnerDrawer,
  TopSticky: LayoutTopSticky,
};
